// Bouncy / sticky landing surfaces (E-14 adjacent). PURE (ENG-1): no three/DOM.
// Slime reflects a fall into a bounce; honey saps horizontal speed and jump. Fall
// damage on these blocks is already cancelled in combat.ts — this adds the *motion*
// half of the interaction (the bounce and the honey drag). The host reads the block
// under the body and applies the returned velocities.

/** Landing surfaces with special motion. */
export type LandSurface = 'slime' | 'honey' | 'bed' | 'normal';

/** Minimum downward impact that produces a bounce (below this it just rests, so a
 *  body doesn't jitter forever on a slime block). */
export const BOUNCE_MIN = 0.1;
/** A bed bounces weakly compared with a slime block (Java ⅔). */
export const BED_BOUNCE = 2 / 3;
/** Horizontal-speed multiplier while standing on / stuck to honey (Java ≈ 0.4). */
export const HONEY_SPEED = 0.4;
/** Jump velocity is capped this low when jumping off a honey block (Java clamps the
 *  honey jump to roughly half). */
export const HONEY_JUMP = 0.22;
/** Gentle descent a body clinging to a honey wall slides at (Java ≈ −0.05). */
export const HONEY_SLIDE_VY = -0.05;

/** Classify the block a body is standing on. */
export function landSurface(blockName: string | undefined): LandSurface {
  if (blockName === 'slime_block') return 'slime';
  if (blockName === 'honey_block') return 'honey';
  if (typeof blockName === 'string' && blockName.endsWith('_bed')) return 'bed';
  return 'normal';
}

/**
 * The upward velocity a body gets when it lands on `surface` with downward
 * velocity `vyBefore` (negative = falling). Slime reflects the impact (Java:
 * motionY = −motionY); a bed reflects ⅔. Sneaking cancels the bounce entirely
 * (you absorb the fall). A rising or negligible impact, or a non-bouncy surface,
 * returns 0.
 */
export function bounceVelocity(surface: LandSurface, vyBefore: number, sneaking: boolean): number {
  if (vyBefore >= -BOUNCE_MIN) return 0;
  if (sneaking) return 0;
  if (surface === 'slime') return -vyBefore;
  if (surface === 'bed') return -vyBefore * BED_BOUNCE;
  return 0;
}

/** Horizontal-velocity multiplier a body on `surface` moves at (honey drags). */
export function surfaceSpeedFactor(surface: LandSurface): number {
  return surface === 'honey' ? HONEY_SPEED : 1;
}

/** Cap a just-applied jump velocity for a body leaving a honey block. Non-honey
 *  surfaces (or a downward/zero vy) pass through unchanged. */
export function honeyJumpCap(surface: LandSurface, vy: number): number {
  if (surface !== 'honey') return vy;
  return vy > HONEY_JUMP ? HONEY_JUMP : vy;
}

/** The descent velocity for a body clinging to a honey wall — clamps a faster fall
 *  to the slow honey slide, but never speeds a gentler motion up. */
export function honeySlideVelocity(vy: number): number {
  return vy < HONEY_SLIDE_VY ? HONEY_SLIDE_VY : vy;
}
